import { useMemo } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { composeAvatarUrl } from '@/lib/avatar';

/**
 * Story-card byline with optional author avatar.
 *
 * Renders the author's avatar (if any) + byline "by {authorName}" + an
 * optional profession suffix ("· 💻 Tech / Engineering"). Gives readers a
 * quick demographic hint without loading the card with full chip rows.
 *
 * `authorName` and `avatarObjectKey` are projected from the
 * `km_public_stories` view — joined server-side from km_user_profiles and
 * gated by `is_anonymous`. The frontend NEVER does a per-story profile
 * fetch from here: that would be N+1 and would also leak the author's
 * UUID, which would defeat the anonymity guarantee for anonymous stories.
 *
 * We show only the first profession even when a story has several —
 * almost every story has one, and a single label keeps cards scannable.
 */
export default function AuthorLine({
  authorName,
  avatarObjectKey = null,
  professions,
  tagOptions,
  className = '',
}) {
  const profession = useMemo(() => {
    if (!Array.isArray(professions) || professions.length === 0) return null;
    const opts = tagOptions?.professions || [];
    if (opts.length === 0) return null; // tag dictionary still loading
    const firstId = professions[0];
    return opts.find((t) => t.id === firstId) || null;
  }, [professions, tagOptions]);

  const avatarUrl = composeAvatarUrl(avatarObjectKey);
  const initials = (authorName || '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="story-author-line">
      {avatarObjectKey ? (
        <Avatar className="h-6 w-6 flex-shrink-0 border border-[#E6E2D6]">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
          <AvatarFallback className="bg-[#FBF4EB] text-[#C05621] text-[10px] font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
      ) : null}
      <p className="text-sm text-[#6B665E]">
        by {authorName}
        {profession ? (
          <>
            <span className="mx-1.5 text-[#6B665E]/60">·</span>
            {profession.emoji && (
              <span aria-hidden className="mr-1">{profession.emoji}</span>
            )}
            <span>{profession.display_name}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}
